import { Box, styled } from '@mui/material';
import type { ReactNode } from 'react';

export interface ItemThumbnailProps {
  thumbnailUri?: string | null;
  size?: 'small' | 'medium';
  fallbackIcon?: ReactNode;
}

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: 'small' | 'medium' }>(({ size }) => ({
  position: 'relative',
  width: size === 'small' ? 32 : 64,
  height: size === 'small' ? 32 : 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Card = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: 'small' | 'medium' }>(({ theme, size }) => {
  const baseSize = size === 'small' ? 28 : 48;

  return {
    position: 'absolute',
    width: baseSize,
    height: baseSize,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[200],
    border: `1px solid ${theme.palette.grey[300]}`,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
});

const ThumbnailImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

/**
 * ItemThumbnail displays a single thumbnail card with consistent styling.
 * - Shows the thumbnail image if available
 * - Falls back to a provided icon if no thumbnail
 * - Works in both list view (small) and grid view (medium)
 */
export const ItemThumbnail = ({
  thumbnailUri,
  size = 'small',
  fallbackIcon,
}: ItemThumbnailProps) => {
  return (
    <Container size={size}>
      <Card size={size}>
        {thumbnailUri ? (
          <ThumbnailImage src={thumbnailUri} crossOrigin="anonymous" alt="" />
        ) : (
          fallbackIcon
        )}
      </Card>
    </Container>
  );
};
